import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  Typography,
  MenuList,
  MenuItem,
  makeStyles,
  Popover,
  alpha,
} from '@material-ui/core';
import CustomButton from '../customButton';
import ConditionalTooltip from '../conditionalTooltip';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import Scrollbars from '../scrollbars';
import { ReactComponent as ArrowDown } from '../../../assets/icons/arrow-down-dark.svg';
import { isEmpty } from '../../../utility/validation';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  popover: {
    display: 'flex',
    width: 210,
    marginTop: spacing(1),
    overflowX: 'visible',
    overflowY: 'visible',
  },
  menuList: {
    display: 'flex',
    padding: spacing(3),
    width: '100%',
    maxHeight: 200,
    minHeight: 0,
    flex: 1,
  },
  menu: {
    boxSizing: 'border-box',
    border: `1px solid ${primary.bluish6}`,
    borderRadius: 4,
    filter: `drop-shadow(0px 4px 10px ${alpha(primary.black, 0.2)})`,
  },
  menuItems: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  menuItem: {
    boxSizing: 'border-box',
    borderBottom: `1px solid ${primary.bluish8}`,
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    padding: spacing(2, 4),
    width: '100%',
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  menuItemLabel: {
    lineHeight: '24px',
  },
  labelIcon: {
    marginRight: spacing(2),
    width: 16,
    height: 16,
    flexShrink: 0,
  },
  iconButton: {
    margin: 0,
    height: 38,
    minWidth: 'fit-content',
    width: 'fit-content',
    padding: spacing(2, 4, 2, 4),
    '&:hover': {
      height: 38,
      borderRadius: 44,
    },
  },
  darkContainer: {
    backgroundColor: primary.blue1,
    borderColor: primary.blue1,
    '&:hover': {
      backgroundColor: primary.blue1,
      borderColor: primary.blue1,
    },
  },
  disabledContainer: {
    opacity: '20%',
    cursor: 'default',
    pointerEvents: 'none',
  },
  darkButtonLabel: {
    color: primary.white,
  },
  darkButtonIcon: {
    '& path': {
      fill: primary.white,
    },
  },
  label: {
    marginRight: spacing(2),
    lineHeight: '24px',
  },
  icon: {
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    width: 24,
    height: 24,
  },
  scrollY: {
    right: -9,
  },
}));

const DropdownMenu = ({
  className = null,
  menuItems = [],
  isDisabled = false,
  disabledTooltipText = '',
  buttonLabel = '',
  isDarkButton = false,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleToggle = event => {
    event.stopPropagation();
    event.preventDefault();

    if (isDisabled) return;

    setAnchorEl(event.currentTarget);
  };

  const handleClose = event => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const handleItemClick = useCallback(
    itemAction => event => {
      event.stopPropagation();
      event.preventDefault();

      itemAction();
      handleClose(event);
    },
    []
  );

  const renderTooltipText = () => (
    <Popover
      anchorEl={anchorEl}
      open={!!anchorEl}
      PaperProps={{
        classes: { root: classes.popover },
      }}
      onClose={handleClose}
      anchorOrigin={{
        horizontal: 'left',
        vertical: 'bottom',
      }}
    >
      <MenuList classes={{ root: classes.menuList, padding: classes.menu }}>
        <Scrollbars verticalScrollClass={classes.scrollY}>
          <div className={classes.menuItems}>
            {menuItems.map((item, index) => {
              const {
                type,
                action,
                label,
                icon: Icon,
                shouldItemRender,
              } = item;
              return shouldItemRender() ? (
                <MenuItem
                  id={type}
                  key={`${type}_${index}`}
                  classes={{
                    root: classes.menuItem,
                  }}
                  onClick={handleItemClick(action)}
                >
                  {Icon && <Icon className={classes.labelIcon} />}
                  <TextBoxWithTooltip
                    className={classes.menuItemLabel}
                    variant="subtitle2"
                    text={label}
                  />
                </MenuItem>
              ) : null;
            })}
          </div>
        </Scrollbars>
      </MenuList>
    </Popover>
  );

  return (
    <ConditionalTooltip
      message={disabledTooltipText}
      addTooltip={isDisabled && isEmpty(disabledTooltipText)}
    >
      <CustomButton
        className={classNames(
          classes.iconButton,
          {
            [classes.darkContainer]: isDarkButton,
            [classes.disabledContainer]: isDisabled,
          },
          className
        )}
        type="darkRounded"
        onClick={handleToggle}
      >
        {isEmpty(buttonLabel) && (
          <>
            <Typography
              className={classNames(classes.label, {
                [classes.darkButtonLabel]: isDarkButton,
              })}
              variant="subtitle1"
            >
              {buttonLabel}
            </Typography>
            <div className={classes.icon}>
              <ArrowDown
                className={classNames({
                  [classes.darkButtonIcon]: isDarkButton,
                })}
              />
            </div>
          </>
        )}
      </CustomButton>
      {renderTooltipText()}
    </ConditionalTooltip>
  );
};

DropdownMenu.propTypes = {
  className: PropTypes.string,
  isDarkButton: PropTypes.bool,
  menuItems: PropTypes.arrayOf(PropTypes.object),
  disabledTooltipText: PropTypes.string,
  isDisabled: PropTypes.bool,
  buttonLabel: PropTypes.string,
};

export default DropdownMenu;
